import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal, LoadingSpinner, Select, useToastActions } from '@airclou/ui';
import { Users } from 'lucide-react';
import {
  listAvailableGroups,
  addGroupToWorkspace,
  WorkspaceGroupAssignmentErrorCodes,
  type AvailableGroup
} from '@/api/workspaces';

interface AddGroupToWorkspaceModalProps {
  workspaceId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddGroupToWorkspaceModal({
  workspaceId,
  isOpen,
  onClose,
  onSuccess
}: AddGroupToWorkspaceModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error: toastError } = useToastActions();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<AvailableGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableGroups();
    }
  }, [isOpen, workspaceId]);

  const fetchAvailableGroups = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const { groups } = await listAvailableGroups(workspaceId);
      setAvailableGroups(groups);
      setSelectedGroupId(groups.length > 0 ? groups[0].id : '');
    } catch (err: any) {
      setFetchError(err.message || t('groups.failedToLoadAvailableGroups'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedGroupId) return;

    setSubmitting(true);
    try {
      await addGroupToWorkspace(workspaceId, selectedGroupId);
      success(t('groups.groupAddedToWorkspace'), t('common.success'));
      onSuccess();
      onClose();
    } catch (err: any) {
      const code = (err as any).code;
      if (code === WorkspaceGroupAssignmentErrorCodes.GROUP_ALREADY_ASSIGNED) {
        toastError(t('groups.groupAlreadyAssigned'), t('common.error'));
      } else if (code === WorkspaceGroupAssignmentErrorCodes.CROSS_COMPANY_ASSIGNMENT) {
        toastError(t('groups.crossCompanyAssignment'), t('common.error'));
      } else {
        toastError(err.message || t('groups.failedToAddGroup'), t('common.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const selectedGroup = availableGroups.find(g => g.id === selectedGroupId);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('groups.addGroupToWorkspace')}
      size="md"
      submitText={submitting ? t('common.adding') : t('common.add') + ' ' + t('common.groups')}
      canSubmit={!!selectedGroupId && !submitting && !loading}
      onSubmit={handleSubmit}
    >
      <div className="space-y-4">
        {loading ? (
          <div className="py-8 flex justify-center">
            <LoadingSpinner text={t('groups.loadingGroups')} />
          </div>
        ) : fetchError ? (
          <div className="py-8 text-center">
            <p className="text-sm text-red-600 dark:text-red-400">{fetchError}</p>
            <button
              onClick={fetchAvailableGroups}
              className="mt-2 text-sm text-primary hover:underline"
            >
              {t('common.tryAgain')}
            </button>
          </div>
        ) : availableGroups.length === 0 ? (
          <div className="py-8 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('groups.noAvailableGroups')}
            </p>
          </div>
        ) : (
          <>
            <div>
              <label
                htmlFor="group"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('groups.selectGroup')}
              </label>
              <Select
                id="group"
                value={selectedGroupId}
                onChange={setSelectedGroupId}
                options={availableGroups.map((group) => ({
                  value: group.id,
                  label: `${group.name} (${group.member_count} ${group.member_count === 1 ? 'member' : 'members'})`,
                }))}
                placeholder={t('groups.selectAGroup')}
                className="w-full"
              />
            </div>

            {selectedGroup?.description && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedGroup.description}
                </p>
              </div>
            )}

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Note:</strong> {t('groups.addGroupNoteText')}
              </p>
            </div>
          </>
        )}
      </div>
    </FormModal>
  );
}
